import { CheckBadgeIcon, MagnifyingGlassPlusIcon, PlusCircleIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@/atoms-react/button/Button.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base, Label } from "@/atoms-react/text/Text.react";
import type { DeclarativeNif } from "@/features/flow/types";
import { isCreateTaxAddressResponseSuccess } from "@/features/register/types";
import { useUser } from "@/features/user/hooks/use-user";
import { useNifValidation } from "@/features/user/nif/hooks/use-nif-validation";
import { useTaxAddress } from "@/features/user/tax_address/hooks/use-taxAdress";
import type { FullCountry, NaturalPerson, NaturalPersonDTO } from "@/features/user/types";
import { COUNTRY_NIF_CHECK_REQUIRED } from "@/features/utils/constants";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { Themes } from "@/types/theme";

type UserIdentityProps = {
  refetchUser?: () => void;
  onInputChange: (userForm: NaturalPersonDTO & { isDirty: boolean }) => void;
  np?: NaturalPerson;
  disabled: boolean;
  disableLvl3?: boolean;
  userForm: NaturalPersonDTO & { isDirty: boolean };
} & React.ComponentPropsWithoutRef<"div">;
export const TaxAdresseIdentity = ({
  np,
  refetchUser,
  disabled,
  disableLvl3 = false,
  onInputChange,
  userForm
}: UserIdentityProps) => {
  const { t } = useTranslation(["pass"]);
  const { locale } = useLocale();

  const labelInputDivClassName = "md:flex  flex-wrap items-center md:space-x-6";
  const labelClassName = "flex w-40 min-w-40 mb-2 md:mb-0";
  const inputClassName = "flex  flex-col grow ";
  const [error, setErrors] = useState<string[] | null>([]);
  const { useCountries } = useUser();
  const { data: countriesData } = useCountries(locale.language);
  const { getNifValidation } = useNifValidation();
  const { mutateAsync: nififValidation, isPending } = getNifValidation();
  const [addressNif, setAddressNif] = useState<DeclarativeNif[]>(np?.TaxAddress ?? []);
  const [addressNif2, setAddressNif2] = useState<DeclarativeNif | null>(null);
  const { createAddressTaxMutation } = useTaxAddress();
  const { mutateAsync: createTaxAddres, isPending: isPendingCreate } = createAddressTaxMutation();

  const handleTaxAddressCreate = async () => {
    if (
      !addressNif2?.country ||
      (addressNif2?.country && addressNif2.tin !== null && !addressNif2.apiCheck)
    ) {
      toast.error(t("flow.id_control.error.must_enter_fields"));
      return;
    }
    if (addressNif.find((tax) => tax.country === addressNif2.country)) {
      toast.error(t("flow.id_control.input.nationality_add_error"));
      return;
    }

    try {
      const createTaxAddressResponse = await createTaxAddres({
        taxAddress: [addressNif2]
      });
      if (!createTaxAddressResponse) {
        toast.error(t("olkypass.general.error_save"));
        return;
      }
      if (isCreateTaxAddressResponseSuccess(createTaxAddressResponse)) {
        toast.success(t("olkypass.general.success_save"));
        setAddressNif((prevState: DeclarativeNif[]) => [
          ...prevState,
          { ...addressNif2, UUID: createTaxAddressResponse[0].uuid }
        ]);
        setAddressNif2(null);
        refetchUser?.();
      }
    } catch (_) {
      toast.error(t("olkypass.general.error_save"));
    }
  };

  const handleNifVerification = async (taxResidence: DeclarativeNif, indexAd: number) => {
    try {
      const countryBirthIsMalta = countriesData?.countries.find(
        (item: FullCountry) => item.id === np?.countryOfBirth && item.alpha2 === "MT"
      );
      const maltaCountryCode = countriesData?.countries.find(
        (item: FullCountry) => item.alpha2.toLocaleUpperCase() === "MT"
      );
      const taxNifCountry = countriesData?.countries.find(
        (item: FullCountry) => item.id.toString() === taxResidence.country
      );
      if (addressNif.filter((ad) => ad.country === taxResidence.country).length > 1) {
        toast.error(t("flow.id_control.input.nationality_add_error"));
        return;
      }

      if (
        COUNTRY_NIF_CHECK_REQUIRED.includes(taxNifCountry?.alpha2 as string) &&
        ((taxResidence.country as string) !== maltaCountryCode?.id.toString() ||
          ((taxResidence.country as string) === maltaCountryCode?.id.toString() &&
            countryBirthIsMalta))
      ) {
        const response = await nififValidation({
          nif: taxResidence.tin as string,
          countryCode: taxNifCountry?.alpha2 as string
        });
        if (response.validSyntax && response.validStructure) {
          setAddressNif((prev: DeclarativeNif[]) => {
            const arrayNifs = [...prev];
            const currentNif = arrayNifs.find((_, index) => {
              return index === indexAd;
            });
            const arrayNifsWithoutCurrent = arrayNifs.filter((_, index) => index !== indexAd);
            const newCurrentNif: DeclarativeNif = {
              ...currentNif,
              country: currentNif?.country ?? null,
              tinCheck: true,
              tinExist: true,
              apiCheck: true,
              tin: currentNif?.tin ?? null,
              allowUpdate: true
            };

            arrayNifsWithoutCurrent.splice(indexAd, 0, newCurrentNif);

            onInputChange({ ...userForm, TaxAddress: arrayNifsWithoutCurrent });

            return [...arrayNifsWithoutCurrent];
          });

          return toast.success(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.valid"));
        } else {
          toast.error(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error"));
          setErrors((prev) => [...(prev ?? []), taxResidence?.country as string]);
          setTimeout(() => {
            setErrors([]);
          }, 2000);
        }
      } else {
        setAddressNif((prev: DeclarativeNif[]) => {
          const arrayNifs: DeclarativeNif[] = [...prev];
          const currentNif = arrayNifs.find((_, index) => {
            return index === indexAd;
          });
          const arrayNifsWithoutCurrent = arrayNifs.filter((_, index) => index !== indexAd);
          const newCurrentNif: DeclarativeNif = {
            ...currentNif,
            country: currentNif?.country ?? null,
            tinCheck: true,
            tinExist: true,
            apiCheck: true,
            allowUpdate: true,
            tin: currentNif?.tin ?? null
          };

          arrayNifsWithoutCurrent.splice(indexAd, 0, newCurrentNif);
          onInputChange({ ...userForm, TaxAddress: arrayNifsWithoutCurrent });
          return [...arrayNifsWithoutCurrent];
        });

        return toast.success(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.valid"));
      }
    } catch (_) {
      return toast.error(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error"));
    }
  };

  const handleNifVerificationAdd = async (taxResidence: DeclarativeNif) => {
    const countryBirthIsMalta = countriesData?.countries.find(
      (item: FullCountry) => item.id === np?.countryOfBirth && item.alpha2 === "MT"
    );
    const maltaCountryCode = countriesData?.countries.find(
      (item: FullCountry) => item.alpha2.toLocaleUpperCase() === "MT"
    );
    const taxNifCountry = countriesData?.countries.find(
      (item: FullCountry) => item.id.toString() === taxResidence.country
    );

    if (addressNif.find((ad) => ad.country === taxResidence.country)) {
      toast.error(t("flow.id_control.input.nationality_add_error"));
      return;
    }
    if (
      COUNTRY_NIF_CHECK_REQUIRED.includes(taxNifCountry?.alpha2 as string) &&
      ((taxResidence.country as string) !== maltaCountryCode?.id.toString() ||
        ((taxResidence.country as string) === maltaCountryCode?.id.toString() &&
          countryBirthIsMalta))
    ) {
      const response = await nififValidation({
        nif: taxResidence.tin as string,
        countryCode: taxNifCountry?.alpha2 as string
      });
      if (response.validSyntax && response.validStructure) {
        toast.success(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.valid"));
        setAddressNif2(
          (prevState) =>
            ({
              ...(prevState as DeclarativeNif),
              tinCheck: true,
              apiCheck: true,
              tinExist: response.validSyntax && response.validStructure
            }) as DeclarativeNif
        );
      } else toast.error(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error"));
    } else {
      setAddressNif2(
        (prevState) =>
          ({
            ...(prevState as DeclarativeNif),
            tinCheck: true,
            apiCheck: true,
            tinExist: true
          }) as DeclarativeNif
      );

      toast.success(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.valid"));
    }
  };
  const errorInputTin =
    addressNif2?.tin &&
    addressNif2?.tin?.length > 0 &&
    !addressNif2.tinExist &&
    addressNif2.apiCheck &&
    addressNif2.tinCheck
      ? t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error")
      : undefined;
  return (
    <>
      <div className={labelInputDivClassName}>
        <></>
        <Label className={labelClassName}>
          {t("olkypass.accountPage.basicInfo.input.country_nif")}
        </Label>
        <div className={inputClassName}>
          <div className="mt-2 flex flex-col">
            {addressNif &&
              addressNif.length > 0 &&
              addressNif.map((ad, indexAd) => (
                <div
                  key={indexAd}
                  className="mt-2 flex flex-row flex-wrap truncate sm:flex-nowrap"
                >
                  <Dropdown
                    value={ad.country as string}
                    disabled={disabled || disableLvl3}
                    onChange={(item) => {
                      if (addressNif.find((ad: DeclarativeNif) => ad.country === item.key)) {
                        toast.error(t("flow.id_control.input.nationality_add_error"));
                      }
                      setAddressNif((prev: DeclarativeNif[]) => {
                        const arraNifs = prev;
                        const currentDeclarativeNif: DeclarativeNif | undefined = arraNifs.find(
                          (_, index) => {
                            return index === indexAd;
                          }
                        );
                        const arrayNifsWithoutCurrent: DeclarativeNif[] = arraNifs.filter(
                          (_, index) => index !== indexAd
                        );
                        const newState: DeclarativeNif = {
                          ...currentDeclarativeNif,
                          country: item.key,
                          tinCheck: true,
                          tinExist: false,
                          apiCheck: false,
                          allowUpdate: true,
                          tin: null
                        };

                        arrayNifsWithoutCurrent.splice(indexAd, 0, newState);
                        onInputChange({ ...userForm, TaxAddress: arrayNifsWithoutCurrent });
                        return [...arrayNifsWithoutCurrent];
                      });
                    }}
                    className="w-full truncate sm:mr-2 sm:w-48 sm:max-w-48"
                    search={true}
                    items={
                      (countriesData?.countries ?? []).map((country) => ({
                        key: country.id.toString(),
                        value: country.defaultName,
                        icon: <Flag countryCode={country.alpha2.toLowerCase()} />
                      })) ?? []
                    }
                  />
                  <InputLabel
                    autoComplete="given-name"
                    placeholder={t("olkypass.accountPage.basicInfo.input.nif")}
                    error={
                      error?.includes(ad.country?.toString() ?? "")
                        ? t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error")
                        : undefined
                    }
                    className="mt-1 sm:mt-0"
                    value={ad?.tin as string}
                    disabled={
                      (disabled && ad.country && ad.tin && ad.apiCheck) ||
                      isPending ||
                      disableLvl3 ||
                      disabled
                    }
                    suffixIcon={
                      ad.tin !== null && ad.tin && ad.tin !== "" && !ad.apiCheck ? (
                        <>
                          <Button
                            size="lg"
                            icon={<MagnifyingGlassPlusIcon />}
                            onClick={(e) => {
                              e.preventDefault();
                              if (
                                (ad?.tin?.length as number) > 0 &&
                                !isPending &&
                                !(ad.country && ad.apiCheck && ad.tinCheck && ad.tinExist)
                              )
                                handleNifVerification(ad, indexAd);
                            }}
                            data-tooltip={t(
                              "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.verify"
                            )}
                            className="text-primary"
                            theme={Themes.iconButton}
                            loading={isPending}
                            disabled={
                              (ad.country && ad.tin && ad.apiCheck && ad.tinCheck && ad.tinExist) ||
                              disableLvl3 ||
                              disabled
                            }
                          ></Button>
                        </>
                      ) : (
                        ad.tin &&
                        ad.apiCheck && (
                          <>
                            <div>
                              {" "}
                              <CheckBadgeIcon className="h-6 w-6 text-primary sm:mb-2 sm:h-8 sm:w-8" />{" "}
                            </div>
                          </>
                        )
                      )
                    }
                    onChange={(value: string) => {
                      setAddressNif((prev: DeclarativeNif[]) => {
                        const arrayNifs: DeclarativeNif[] = [...prev];
                        const currentNif: DeclarativeNif | undefined = arrayNifs.find(
                          (_, index) => {
                            return index === indexAd;
                          }
                        );
                        const arrayNifsWithoutCurrent: DeclarativeNif[] = arrayNifs.filter(
                          (_, index) => index !== indexAd
                        );
                        const newCurrentNif: DeclarativeNif = {
                          ...currentNif,
                          country: currentNif?.country ?? null,
                          tinCheck: true,
                          tinExist: false,
                          apiCheck: false,
                          tin: value.toUpperCase()
                        };

                        arrayNifsWithoutCurrent.splice(indexAd, 0, newCurrentNif);
                        onInputChange({ ...userForm, TaxAddress: arrayNifsWithoutCurrent });
                        return [...arrayNifsWithoutCurrent];
                      });
                    }}
                  />{" "}
                </div>
              ))}

            {addressNif2 && addressNif2.tinExist && (
              <div className="mt-6 flex w-full grow flex-col sm:flex-row">
                {" "}
                <Dropdown
                  value={addressNif2.country}
                  placeholder={t("flow.id_control.input.business.country_label")}
                  onChange={(item) => {
                    if (addressNif.find((ta) => ta.country === item.key.toString()))
                      toast.error(t("flow.id_control.input.nationality_add_error"));
                    setAddressNif2((prev) => ({
                      ...(prev as DeclarativeNif),
                      country: item.key.toString()
                    }));
                  }}
                  className="w-full sm:mr-2 sm:w-48"
                  search={true}
                  items={
                    (countriesData?.countries ?? []).map((country) => ({
                      key: country.id.toString(),
                      value: country.defaultName,
                      icon: <Flag countryCode={country.alpha2.toLowerCase()} />
                    })) ?? []
                  }
                />
                <InputLabel
                  autoComplete="given-name"
                  placeholder={t("olkypass.accountPage.basicInfo.input.nif")}
                  className={twMerge(`w-full`)}
                  error={errorInputTin}
                  value={addressNif2?.tin as string}
                  disabled={
                    (isPendingCreate && addressNif2.apiCheck && !addressNif2.country) || disableLvl3
                  }
                  onChange={(value) => {
                    setAddressNif2((prev) => ({
                      ...(prev as DeclarativeNif),
                      tin: value.toUpperCase(),
                      apiCheck: false
                    }));
                  }}
                  suffixIcon={
                    <>
                      {(addressNif2.country && addressNif2.tin !== null && addressNif2.apiCheck) ||
                      (addressNif2.country && addressNif2.tin === null) ? (
                        !isPendingCreate ? (
                          <Button
                            theme={Themes.iconButton}
                            icon={<PlusCircleIcon />}
                            size="lg"
                            className={twMerge("text-primary")}
                            onClick={(e) => {
                              e.preventDefault();
                              handleTaxAddressCreate();
                            }}
                          />
                        ) : (
                          <Loader />
                        )
                      ) : (
                        addressNif2.tin !== null &&
                        !addressNif2.apiCheck && (
                          <Button
                            icon={<MagnifyingGlassPlusIcon />}
                            theme={Themes.iconButton}
                            className="text-primary"
                            onClick={(e) => {
                              e.preventDefault();
                              if (
                                (addressNif2?.tin?.length as number) > 0 &&
                                !isPending &&
                                !(
                                  addressNif2.country &&
                                  addressNif2.apiCheck &&
                                  addressNif2.tinCheck &&
                                  addressNif2.tinExist
                                )
                              )
                                handleNifVerificationAdd(addressNif2);
                            }}
                            data-tooltip={t(
                              "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.verify"
                            )}
                            loading={isPending}
                            disabled={
                              (addressNif2.country &&
                                addressNif2.tin &&
                                addressNif2.apiCheck &&
                                addressNif2.tinCheck &&
                                addressNif2.tinExist) ||
                              disableLvl3
                            }
                          ></Button>
                        )
                      )}
                    </>
                  }
                />
              </div>
            )}
            {(addressNif?.length as number) < 4 && !disableLvl3 && (
              <>
                <div className="mt-2 mb-2">
                  <Base className="mt-4 text-base">
                    {t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.check")}
                  </Base>
                  <div className="mt-4 flex w-full flex-row">
                    <Checkbox
                      value={addressNif2?.tinExist === true}
                      onChange={() => {
                        setAddressNif2({
                          apiCheck: false,
                          tin: null,
                          tinExist: true,
                          country: null,
                          tinCheck: false
                        });
                      }}
                    >
                      <Base className="text-base">
                        {" "}
                        {t("flow.id_control.input.add_nationality_checkbox_yes")}
                      </Base>
                    </Checkbox>
                    <Checkbox
                      className="ml-3"
                      value={addressNif2?.tinExist !== true}
                      onChange={() => {
                        setAddressNif2(null);
                      }}
                    >
                      <Base className="text-base">
                        {" "}
                        {t("flow.id_control.input.add_nationality_checkbox_no")}
                      </Base>
                    </Checkbox>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
